export interface MerchantComplianceProfile {
  gstNumber?: string;
  fssaiNumber?: string;
  tradeLicenseNumber?: string;
  ownerName?: string;
  businessName?: string;
  address?: string;
  city?: string;
  latitude?: string;
  longitude?: string;
  sources: string[];
}

type DocRow = {
  config?: { code?: string; name?: string };
  uploaded?: { documentNumber?: string; status?: string };
  documentStatus?: string;
};

function normCode(code: string) {
  return code.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function pickApprovedDocNumber(docs: DocRow[], matchers: string[]): string | undefined {
  for (const row of docs) {
    const code = normCode(row.config?.code || row.config?.name || '');
    if (!matchers.some((m) => code.includes(m))) continue;
    const approved = row.documentStatus === 'APPROVED' || row.uploaded?.status === 'approved';
    if (!approved) continue;
    const num = row.uploaded?.documentNumber?.trim();
    if (num) return num;
  }
  return undefined;
}

export function extractMerchantCompliance(data: {
  documents?: DocRow[];
  onboarding?: { formData?: Record<string, unknown>; status?: string };
}): MerchantComplianceProfile {
  const docs = data.documents || [];
  const form = (data.onboarding?.formData || {}) as Record<string, unknown>;
  const sources: string[] = [];

  const gstFromDoc = pickApprovedDocNumber(docs, ['gst', 'gstin']);
  const fssaiFromDoc = pickApprovedDocNumber(docs, ['fssai', 'foodlicense']);
  const tradeFromDoc = pickApprovedDocNumber(docs, ['trade', 'tradelicense', 'shopact']);

  const gstNumber = gstFromDoc || String(form.gst_number || form.gstNumber || '').trim() || undefined;
  const fssaiNumber = fssaiFromDoc || String(form.fssai_license || form.fssaiNumber || form.fssai || '').trim() || undefined;
  const tradeLicenseNumber = tradeFromDoc || String(form.trade_license || form.tradeLicense || '').trim() || undefined;

  if (gstFromDoc) sources.push('GST document');
  else if (gstNumber) sources.push('onboarding form');
  if (fssaiFromDoc) sources.push('FSSAI document');
  else if (fssaiNumber) sources.push('onboarding form');
  if (tradeFromDoc) sources.push('trade license document');
  else if (tradeLicenseNumber) sources.push('onboarding form');

  const ownerName = String(form.owner_name || form.ownerName || '').trim() || undefined;
  const businessName = String(form.business_name || form.businessName || '').trim() || undefined;
  const address = String(form.address || form.addressLine || '').trim() || undefined;
  const city = String(form.city || '').trim() || undefined;
  const lat = form.latitude ?? form.lat;
  const lng = form.longitude ?? form.lng;

  return {
    gstNumber,
    fssaiNumber,
    tradeLicenseNumber,
    ownerName,
    businessName,
    address,
    city,
    latitude: lat != null && lat !== '' ? String(lat) : undefined,
    longitude: lng != null && lng !== '' ? String(lng) : undefined,
    sources: [...new Set(sources)],
  };
}

export function hasMerchantCompliance(profile: MerchantComplianceProfile) {
  return !!(profile.gstNumber || profile.fssaiNumber || profile.tradeLicenseNumber
    || profile.ownerName || profile.businessName || profile.address);
}
